import type { ComponentType, ReactNode } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconClockOutline18 as ClockIcon,
	IconGlobe2Outline18 as GlobeIcon,
	IconCalendarOutline18 as JoinedIcon,
	IconTreePalmOutline18 as TreePalm,
} from "nucleo-ui-outline-18"

import type { MembershipRole } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/cn"
import { isOutOfOffice } from "@productlane/lib/src/out-of-office"
import { Badge, CopyButton, Tooltip } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import {
	HoverCard,
	HoverCardContent,
	HoverCardPortal,
	HoverCardTrigger,
} from "@/components/hover-card"
import { PresenceAvatar } from "@/components/presence-avatar"
import { api } from "@/lib/api"
import { formatPresenceLabel } from "@/lib/format-last-seen"
import { formatLocalTime } from "@/lib/format-local-time"
import { useIsMemberOnline } from "@/lib/states/presence-state"

const ROLE_LABEL: Record<MembershipRole, string> = {
	ADMIN: "Admin",
	USER: "Member",
	VIEWER: "Viewer",
}

interface MemberHoverCardProps {
	userId: string | null | undefined
	children: ReactNode
	className?: string
	side?: "top" | "right" | "bottom" | "left"
	align?: "start" | "center" | "end"
	/** Shown as a plain tooltip when there is no teammate behind the trigger. */
	fallbackLabel?: string
}

/**
 * Wraps any avatar or name that stands for a teammate, so the same card appears
 * wherever a member is shown.
 */
export function MemberHoverCard({
	userId,
	children,
	className,
	side = "right",
	align = "start",
	fallbackLabel,
}: MemberHoverCardProps) {
	if (!userId) {
		if (!fallbackLabel) return <>{children}</>
		return (
			<Tooltip label={fallbackLabel} shouldWrapChildren>
				{children}
			</Tooltip>
		)
	}

	return (
		<HoverCard openDelay={350} closeDelay={120}>
			<HoverCardTrigger asChild>
				<span className={cn("inline-flex min-w-0", className)}>{children}</span>
			</HoverCardTrigger>
			<HoverCardPortal>
				<HoverCardContent
					side={side}
					align={align}
					className="w-72 p-0"
					onClick={(e) => e.stopPropagation()}
				>
					<MemberHoverCardBody userId={userId} />
				</HoverCardContent>
			</HoverCardPortal>
		</HoverCard>
	)
}

function MemberHoverCardBody({ userId }: { userId: string }) {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [membership] = useQuery(queries.membershipByUserId({ userId }))
	const isOnline = useIsMemberOnline(userId)
	const { data: lastSeenByUser } = api.workspace.memberLastSeen.useQuery(
		undefined,
		{ staleTime: 60_000 },
	)
	const lastSeen = lastSeenByUser?.[userId]
	const localTime = formatLocalTime(lastSeen?.timeZone)

	if (!membership?.user) {
		return (
			<div className="p-3 text-sm text-tertiary">
				This person is no longer in the workspace.
			</div>
		)
	}

	const user = membership.user
	const name = user.name || user.email
	const role = membership.role as MembershipRole

	return (
		<div className="flex flex-col">
			<div className="flex items-center gap-3 p-3">
				<PresenceAvatar
					userId={userId}
					size="lg"
					name={name}
					image={user.imageUrl}
					roundness="full"
					dotSize="sm"
				/>
				<div className="flex min-w-0 flex-col gap-0.5">
					<div className="flex min-w-0 items-center gap-2">
						<span className="truncate text-sm font-medium">{name}</span>
						<Badge variant="dark-gray">{ROLE_LABEL[role]}</Badge>
						<OutOfOfficeBadge until={membership.outOfOfficeUntil} />
					</div>
					<div className="group/email flex min-w-0 items-center gap-1.5 text-xs text-secondary">
						<span className="truncate">{user.email}</span>
						<CopyButton
							value={user.email}
							className="flex-none opacity-0 transition-opacity duration-100 group-hover/email:opacity-100"
						/>
					</div>
				</div>
			</div>

			<div className="border-t border-gray-200 dark:border-gray-700/80" />

			<div className="flex flex-col p-3 text-xs">
				<OutOfOfficeFact membership={membership} />
				<MemberFactRow
					Icon={ClockIcon}
					label={formatPresenceLabel(isOnline, lastSeen?.lastSeenAt)}
				/>
				{localTime && (
					<MemberFactRow Icon={GlobeIcon} label={`${localTime} local time`} />
				)}
				<MemberFactRow
					Icon={JoinedIcon}
					label={`Joined ${new Date(membership.createdAt).toLocaleDateString()}`}
				/>
			</div>

			{pathname !== `/members/${userId}` && (
				<button
					type="button"
					onClick={() =>
						void navigate({ to: "/members/$id", params: { id: userId } })
					}
					className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2 text-xs font-medium text-secondary hover:bg-gray-100 dark:border-gray-700/80 dark:hover:bg-gray-800"
				>
					View member details
					<ChevronRight className="h-3.5 w-3.5 flex-none" />
				</button>
			)}
		</div>
	)
}

/** Every fact keeps the same row height, so a copy button never widens the gap. */
export function MemberFactRow({
	Icon,
	label,
	children,
}: {
	Icon: ComponentType<{ className?: string }>
	label: string
	children?: ReactNode
}) {
	return (
		<div className="group/fact flex h-6 min-w-0 items-center gap-2">
			<Icon className="h-3.5 w-3.5 flex-none text-tertiary" />
			<span className="min-w-0 truncate text-secondary">{label}</span>
			{children}
		</div>
	)
}

export function OutOfOfficeBadge({ until }: { until?: number | null }) {
	if (!isOutOfOffice(until)) return null
	return (
		<Badge variant="amber" Icon={TreePalm} className="flex-none">
			Away
		</Badge>
	)
}

/** Until when, and who has the threads meanwhile. */
export function OutOfOfficeFact({
	membership,
}: {
	membership: {
		outOfOfficeUntil?: number | null
		outOfOfficeCoverId?: string | null
	}
}) {
	const [teamMembers] = useQuery(queries.teamMembers())
	if (!isOutOfOffice(membership.outOfOfficeUntil)) return null
	const cover = teamMembers.find(
		(member) => member.userId === membership.outOfOfficeCoverId,
	)
	const coverName = (cover?.user?.name ?? cover?.user?.email)?.split(" ")[0]
	const until = `Out of office until ${format(membership.outOfOfficeUntil!, "d MMM")}`
	return (
		<MemberFactRow
			Icon={TreePalm}
			label={coverName ? `${until}. ${coverName} covers.` : until}
		/>
	)
}
